import type { ApprovalChallenge, ApprovalProof } from "@pagerless/schema";
import {
  type AuthenticationResponseJSON,
  browserSupportsWebAuthn,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
  type RegistrationResponseJSON,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";

/** The device's passkey (Face ID, Touch ID, fingerprint, security key). Injected so tests and mock mode can fake it. */
export interface Passkey {
  /** Sign an approval challenge for a risky fix. */
  approve(challenge: ApprovalChallenge, now: number): Promise<ApprovalProof>;
  login(options: Record<string, unknown>): Promise<AuthenticationResponseJSON>;
  register(options: Record<string, unknown>): Promise<RegistrationResponseJSON>;
}

/** The person closed the Face ID prompt or it timed out. Nothing was changed. */
export class PasskeyCancelled extends Error {
  constructor(options?: { cause?: unknown }) {
    super("Face ID was cancelled. Nothing was changed.", options);
    this.name = "PasskeyCancelled";
  }
}

/** This browser can't use a passkey at all (no WebAuthn, or no platform authenticator). */
export class PasskeyUnavailable extends Error {
  constructor(options?: { cause?: unknown }) {
    super("This browser can't use a passkey.", options);
    this.name = "PasskeyUnavailable";
  }
}

/**
 * WebAuthn errors by type. NotAllowedError is both "cancelled" and "no passkey here" (browsers hide
 * which, on purpose); the screen tells them apart with `hasPasskey`.
 */
export function mapPasskeyError(err: unknown): Error {
  const name = err instanceof Error ? err.name : "";
  const message = err instanceof Error ? err.message : "";
  if (name === "NotAllowedError" || name === "AbortError") return new PasskeyCancelled({ cause: err });
  if (name === "NotSupportedError" || /not supported/i.test(message)) return new PasskeyUnavailable({ cause: err });
  return err instanceof Error ? err : new Error(String(err));
}

function mapError(err: unknown): never {
  throw mapPasskeyError(err);
}

export const webauthnPasskey: Passkey = {
  async approve(c, now) {
    if (!browserSupportsWebAuthn()) throw new PasskeyUnavailable();
    const optionsJSON: PublicKeyCredentialRequestOptionsJSON = {
      challenge: c.challenge,
      userVerification: "required",
      timeout: Math.max(1_000, c.expiresAt - now),
    };
    const r = await startAuthentication({ optionsJSON }).catch(mapError);
    return {
      method: "passkey",
      credentialId: r.id,
      clientDataJSON: r.response.clientDataJSON,
      authenticatorData: r.response.authenticatorData,
      signature: r.response.signature,
    };
  },
  login(options) {
    // The server's options were shape-checked by the api client; simplewebauthn validates the rest.
    return startAuthentication({ optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON }).catch(
      mapError,
    );
  },
  register(options) {
    return startRegistration({ optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON }).catch(
      mapError,
    );
  },
};

/** Mock mode: behaves like a passkey that the person accepts after a short pause. */
export function fakePasskey(delayMs = 500): Passkey {
  const wait = () => new Promise((r) => setTimeout(r, delayMs));
  const b64 = (s: string) => btoa(s).replace(/=+$/, "").replace(/\+/g, "-").replace(/\//g, "_");
  return {
    async approve(c) {
      await wait();
      return {
        method: "passkey",
        credentialId: "mock-credential",
        clientDataJSON: b64(JSON.stringify({ type: "webauthn.get", challenge: c.challenge })),
        authenticatorData: b64("mock-authenticator"),
        signature: b64("mock-signature"),
      };
    },
    async login() {
      await wait();
      return {
        id: "mock-credential",
        rawId: "mock-credential",
        type: "public-key",
        clientExtensionResults: {},
        response: { clientDataJSON: b64("{}"), authenticatorData: b64("a"), signature: b64("s") },
      };
    },
    async register() {
      await wait();
      return {
        id: "mock-credential",
        rawId: "mock-credential",
        type: "public-key",
        clientExtensionResults: {},
        response: { clientDataJSON: b64("{}"), attestationObject: b64("a") },
      };
    },
  };
}
